import { CheckCircle2, Circle, Clock } from "lucide-react"
import type { Coach } from "../../../types/coach-os.types"

interface IntakeContextPanelProps {
  coach: Coach
}

// Helper function to safely render values that might be objects
const renderValue = (value: any): string => {
  if (typeof value === "string") return value
  if (typeof value === "object" && value !== null) {
    // Pretty print nested objects
    return JSON.stringify(value, null, 2)
  }
  return String(value)
}

interface IntakeContext {
  goals?: {
    description: string
    priority: "must-have" | "nice-to-have" | "stretch"
    targetMetric?: string
    whyItMatters?: string
  }[]
  baseline?: {
    currentState?: string
    pastAttempts?: string
    whatWorked?: string
    whatDidnt?: string
  }
  availability?: {
    weeklyHours?: number
    preferredDays?: string[]
    timeOfDay?: string
  }
  constraints?: string[]
  motivation?: string
  additionalContext?: string
}

export default function IntakeContextPanel({ coach }: IntakeContextPanelProps) {
  const intakeContext = (coach.intakeContext as IntakeContext) || {}

  // Determine which sections have been filled
  const hasGoals = intakeContext.goals && intakeContext.goals.length > 0
  const hasBaseline = intakeContext.baseline && Object.values(intakeContext.baseline).some((v) => v)
  const hasAvailability = intakeContext.availability && Object.values(intakeContext.availability).some((v) => v)
  const hasConstraints = intakeContext.constraints && intakeContext.constraints.length > 0
  const hasMotivation = !!intakeContext.motivation

  const sections = [
    { key: "goals", label: "Goals", filled: hasGoals },
    { key: "baseline", label: "Baseline", filled: hasBaseline },
    { key: "availability", label: "Availability", filled: hasAvailability },
    { key: "constraints", label: "Constraints", filled: hasConstraints },
    { key: "motivation", label: "Motivation", filled: hasMotivation },
  ]

  return (
    <div className="w-80 border-l border-border bg-background flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-sm">Intake Progress</h3>
        <p className="text-xs text-muted-foreground mt-1">
          {coach.intakeStatus === "completed"
            ? "Intake completed"
            : coach.intakeStatus === "in-progress"
              ? "Gathering information..."
              : "Not started"}
        </p>
      </div>

      {/* Progress Checklist */}
      <div className="p-4 space-y-2 border-b border-border">
        {sections.map((section) => (
          <div key={section.key} className="flex items-center gap-2 text-sm">
            {section.filled ? (
              <CheckCircle2 className="h-4 w-4 text-primary" />
            ) : coach.intakeStatus === "in-progress" ? (
              <Clock className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Circle className="h-4 w-4 text-muted-foreground" />
            )}
            <span className={section.filled ? "text-foreground" : "text-muted-foreground"}>{section.label}</span>
          </div>
        ))}
      </div>

      {/* Extracted Context */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Goals */}
        {hasGoals && (
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
              <span>🎯</span> Goals
            </h4>
            <div className="space-y-2">
              {intakeContext.goals.map((goal, idx) => (
                <div key={idx} className="text-xs bg-secondary p-2 rounded">
                  <div className="font-medium">{goal.description}</div>
                  {goal.priority && <div className="text-muted-foreground mt-1">Priority: {goal.priority}</div>}
                  {goal.targetMetric && <div className="text-muted-foreground mt-1">Target: {goal.targetMetric}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Baseline */}
        {hasBaseline && (
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
              <span>📊</span> Current State
            </h4>
            <div className="text-xs space-y-1 text-muted-foreground">
              {intakeContext.baseline.currentState && (
                <div>
                  <span className="font-medium text-foreground">Now:</span>{" "}
                  <pre className="whitespace-pre-wrap font-sans">
                    {renderValue(intakeContext.baseline.currentState)}
                  </pre>
                </div>
              )}
              {intakeContext.baseline.pastAttempts && (
                <div>
                  <span className="font-medium text-foreground">Past attempts:</span>{" "}
                  <pre className="whitespace-pre-wrap font-sans">
                    {renderValue(intakeContext.baseline.pastAttempts)}
                  </pre>
                </div>
              )}
              {intakeContext.baseline.whatWorked && (
                <div>
                  <span className="font-medium text-foreground">What worked:</span>{" "}
                  <pre className="whitespace-pre-wrap font-sans">{renderValue(intakeContext.baseline.whatWorked)}</pre>
                </div>
              )}
              {intakeContext.baseline.whatDidnt && (
                <div>
                  <span className="font-medium text-foreground">What didn&apos;t:</span>{" "}
                  <pre className="whitespace-pre-wrap font-sans">{renderValue(intakeContext.baseline.whatDidnt)}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Availability */}
        {hasAvailability && (
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
              <span>⏰</span> Availability
            </h4>
            <div className="text-xs space-y-1 text-muted-foreground">
              {intakeContext.availability.weeklyHours && (
                <div>
                  <span className="font-medium text-foreground">Time:</span> {intakeContext.availability.weeklyHours}{" "}
                  hours/week
                </div>
              )}
              {intakeContext.availability.preferredDays && intakeContext.availability.preferredDays.length > 0 && (
                <div>
                  <span className="font-medium text-foreground">Days:</span>{" "}
                  {intakeContext.availability.preferredDays.join(", ")}
                </div>
              )}
              {intakeContext.availability.timeOfDay && (
                <div>
                  <span className="font-medium text-foreground">Preferred:</span> {intakeContext.availability.timeOfDay}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Constraints */}
        {hasConstraints && (
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
              <span>⚠️</span> Constraints
            </h4>
            <ul className="text-xs space-y-1 list-disc list-inside text-muted-foreground">
              {intakeContext.constraints.map((constraint, idx) => (
                <li key={idx}>{constraint}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Motivation */}
        {hasMotivation && (
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
              <span>💪</span> Motivation
            </h4>
            <div className="text-xs text-muted-foreground">{intakeContext.motivation}</div>
          </div>
        )}

        {/* Empty State */}
        {!hasGoals && !hasBaseline && !hasAvailability && !hasConstraints && !hasMotivation && (
          <div className="text-center text-muted-foreground text-xs py-8">
            <p>Context will appear here as the conversation progresses.</p>
          </div>
        )}
      </div>
    </div>
  )
}
